import { Link } from 'react-router-dom';
import { 
  Building2, 
  FileText, 
  Home, 
  Scale, 
  Users, 
  Gavel,
  ArrowRight 
} from 'lucide-react';
import { Button } from './ui/button';

export const practiceAreas = [
  {
    id: 'civil-litigation',
    icon: Scale,
    title: 'Civil Litigation in Patna',
    shortDesc: 'Comprehensive civil litigation services before courts in Patna and Bihar.',
    description: 'Expert representation in civil suits, injunctions, declaratory suits, and other civil matters before the District Courts and Patna High Court.',
    slug: '/practice-areas/civil-litigation-patna',
    featured: true
  },
  {
    id: 'property-disputes',
    icon: Home,
    title: 'Property & Land Disputes',
    shortDesc: 'Resolution of property ownership, boundary, and land-related conflicts.',
    description: 'Handling of property disputes including ownership claims, encroachment matters, boundary disputes, and mutation proceedings in Bihar.',
    slug: '/practice-areas/property-land-disputes-bihar',
    featured: true
  },
  {
    id: 'contract-disputes',
    icon: FileText,
    title: 'Contract & Commercial Disputes',
    shortDesc: 'Legal assistance in contract enforcement and commercial litigation.',
    description: 'Representation in matters of contract breach, recovery suits, partnership disputes, and commercial litigation.',
    slug: '/practice-areas/contract-commercial-disputes'
  },
  {
    id: 'rent-eviction',
    icon: Building2,
    title: 'Rent & Eviction Matters',
    shortDesc: 'Legal services for landlord-tenant disputes and eviction proceedings.',
    description: 'Assistance in rent control matters, eviction suits, and disputes between landlords and tenants under Bihar tenancy laws.',
    slug: '/practice-areas/rent-eviction-matters'
  },
  {
    id: 'title-partition',
    icon: Users,
    title: 'Title & Partition Suits',
    shortDesc: 'Legal proceedings for property title verification and partition.',
    description: 'Handling of partition suits, title disputes, succession matters, and joint family property division cases.',
    slug: '/practice-areas/title-partition-suits'
  },
  {
    id: 'civil-appeals',
    icon: Gavel,
    title: 'Civil Appeals - Patna High Court',
    shortDesc: 'Appellate practice before the Patna High Court.',
    description: 'Filing and arguing civil appeals, revision petitions, and writ petitions before the Patna High Court.',
    slug: '/practice-areas/civil-appeals-patna-high-court',
    featured: true
  }
];

export const PracticeAreasSection = () => {
  return (
    <section 
      id="practice-areas" 
      className="py-20 lg:py-28 bg-white"
      data-testid="practice-areas-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-[#C6A34A] uppercase tracking-widest text-sm font-medium mb-3">
            Areas of Practice
          </p>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1C2D] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Civil Litigation Services
          </h2>
          <div className="w-20 h-1 bg-[#C6A34A] mx-auto mb-6" />
          <p className="text-[#4A5568] max-w-2xl mx-auto">
            Providing legal representation across various civil matters in Patna and throughout Bihar
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {practiceAreas.map((area, index) => (
            <Link
              key={area.id}
              to={area.slug}
              className={`group bg-[#F8F6F2] p-8 border border-gray-100 hover:border-[#C6A34A]/40 hover:shadow-lg transition-all duration-300 ${
                area.featured ? 'lg:col-span-1' : ''
              }`}
              data-testid={`practice-area-card-${area.id}`}
            >
              <div className="w-14 h-14 bg-[#0B1C2D] flex items-center justify-center mb-6 group-hover:bg-[#C6A34A] transition-colors duration-300">
                <area.icon className="w-7 h-7 text-[#C6A34A] group-hover:text-white transition-colors duration-300" />
              </div>
              
              <h3 
                className="text-xl font-semibold text-[#0B1C2D] mb-3 group-hover:text-[#C6A34A] transition-colors"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {area.title}
              </h3>
              
              <p className="text-[#4A5568] text-sm leading-relaxed mb-4">
                {area.shortDesc}
              </p>
              
              <div className="flex items-center text-[#C6A34A] text-sm font-medium">
                <span>Learn More</span>
                <ArrowRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-[#718096] mb-4">
            Have a legal query about civil matters?
          </p>
          <Button 
            onClick={() => {
              const element = document.getElementById('inquiry-form');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-[#0B1C2D] hover:bg-[#1A2E44] text-white px-8 py-6"
            data-testid="practice-areas-cta"
          >
            Submit Your Legal Query
          </Button>
        </div>
      </div>
    </section>
  );
};
